import React, { useState } from 'react';
import {
  Modal,
  FormField,
  Input,
  Textarea,
  Button,
  SpaceBetween,
  Icon,
  Box,
} from '@cloudscape-design/components';
import { toLower } from 'lodash';
import {
  ASSIGNEE_LABELS,
  ERROR_MESSAGES,
  VALIDATION_PATTERNS,
} from 'src/contstant/commonLabels';
import RequiredFieldLabel from '../RequiredFieldLabel';

interface ChangeAssigneeModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (data: { newAssignee: string; comment: string }) => void;
  currentAssignee?: string;
}

export const ChangeAssigneeModal: React.FC<ChangeAssigneeModalProps> = ({
  visible,
  onDismiss,
  onConfirm,
  currentAssignee,
}) => {
  const [newAssignee, setNewAssignee] = useState('');
  const [comment, setComment] = useState('');
  const [errors, setErrors] = useState<{ assignee?: string; comment?: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: { assignee?: string; comment?: string } = {};

    if (!newAssignee.trim()) {
      newErrors.assignee = 'Assignee name is required';
    } else if (!VALIDATION_PATTERNS.ASSIGNEE_ALIAS.test(newAssignee)) {
      newErrors.assignee = ERROR_MESSAGES.ASSIGNEE_AT_SYMBOL;
    }

    if (!comment.trim()) {
      newErrors.comment = 'Comment is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setIsLoading(true);
      try {
        await onConfirm({
          newAssignee: toLower(newAssignee.trim()),
          comment: comment.trim(),
        });
        setNewAssignee('');
        setComment('');
        setErrors({});
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDismiss = () => {
    setNewAssignee('');
    setComment('');
    setErrors({});
    onDismiss();
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header={
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Icon name="user-profile" variant="normal" />
          <span>Change Task Assignee</span>
        </SpaceBetween>
      }
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="normal"
              onClick={handleDismiss}
              disabled={isLoading}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isLoading}
              disabled={isLoading}
              data-testid="confirm-button"
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <FormField label={ASSIGNEE_LABELS.LABEL} errorText={errors.assignee}>
          <Input
            value={newAssignee}
            onChange={({ detail }) => setNewAssignee(detail.value)}
            placeholder={ASSIGNEE_LABELS.PLACEHOLDER}
            data-testid="enter-assignee-name-field"
          />
        </FormField>

        <FormField
          label={<RequiredFieldLabel label="Comment" />}
          errorText={errors.comment}
        >
          <Textarea
            value={comment}
            onChange={({ detail }) => setComment(detail.value)}
            placeholder="Explain why you're changing the assignee"
            rows={3}
            data-testid="comment-field"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
